import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Countdown from 'react-countdown';
import API from '../utils/axios';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { TermsModal } from '../components/TermsModal';
import { InlinePredictionCard } from '../components/InlinePredictionCard';
import { Trophy, Users, Target, Gift, FileText, Ticket, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '../contexts/SettingsContext';
import { buildAssetUrl } from '../contexts/SettingsContext';

export const Home = () => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [matches, setMatches] = useState([]);
  const [partners, setPartners] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [showTerms, setShowTerms] = useState(false);
  const [ticketCode, setTicketCode] = useState('');
  const [loadError, setLoadError] = useState(null);

  const handleCheckTicket = (e) => {
    e.preventDefault();
    const code = ticketCode.trim();
    if (!code) {
      toast.error('Masukkan kode tiket terlebih dahulu');
      return;
    }
    navigate(`/check-ticket?code=${encodeURIComponent(code)}`);
  };

  useEffect(() => {
    (async () => {
      try {
        const [m, p, w] = await Promise.all([
          API.get('/matches?status=upcoming&limit=12'),
          API.get('/partners', { params: { _t: Date.now() } }),
          API.get('/websites'),
        ]);
        setMatches(m.data);
        setPartners(p.data);
        setWebsites(w.data);
        setLoadError(null);
      } catch (e) {
        console.error('Failed to load home data', e);
        setLoadError(
          'Gagal memuat data. Server sedang tidak merespons — mohon muat ulang halaman beberapa saat lagi.'
        );
      }
    })();
  }, []);

  const countdownRenderer = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) return <span className="text-2xl font-black">Event Dimulai!</span>;
    return (
      <div className="grid grid-cols-4 gap-4">
        {[['Hari', days], ['Jam', hours], ['Menit', minutes], ['Detik', seconds]].map(([l, v]) => (
          <div key={l} className="text-center">
            <div className="text-3xl font-black text-[#FFD60A] tabular-nums">{v}</div>
            <div className="text-xs text-gray-400 mt-1">{l}</div>
          </div>
        ))}
      </div>
    );
  };

  // Hero countdown renderer only (per-match countdown handled inside InlinePredictionCard)

  return (
    <div className="App">
      <Header />

      <section
        className="hero-section"
        style={{ backgroundImage: `url(${buildAssetUrl(settings?.hero_background) || 'https://images.unsplash.com/photo-1706675780107-7c43cc487928?crop=entropy&cs=srgb&fm=jpg&q=85'})` }}
        data-testid="hero-section"
      >
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid gap-6 min-h-[90vh] items-center py-20">
            <div className="space-y-6 max-w-2xl text-left">
              {settings?.hero_title && (
                <h1
                  className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight drop-shadow-[0_4px_20px_rgba(0,0,0,0.7)]"
                  data-testid="hero-title"
                >
                  {settings.hero_title}
                </h1>
              )}
              {settings?.hero_subtitle && (
                <p
                  className="text-base md:text-lg lg:text-xl text-white font-semibold leading-relaxed max-w-xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]"
                  data-testid="hero-subtitle"
                >
                  {settings.hero_subtitle}
                </p>
              )}
              <div className="flex flex-wrap gap-4 pt-2">
                <button onClick={() => navigate('/schedule')} className="btn-primary" data-testid="cta-primary-btn">
                  {settings?.primary_button_text || 'Mulai Tebak Skor'}
                </button>
                <button onClick={() => navigate('/schedule')} className="btn-secondary" data-testid="cta-secondary-btn">
                  {settings?.secondary_button_text || 'Jadwal Hari Ini'}
                </button>
              </div>
            </div>

            <div
              className={
                settings?.enable_countdown && settings?.countdown_date
                  ? 'grid grid-cols-1 md:grid-cols-3 gap-4 mt-4'
                  : 'grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 max-w-3xl'
              }
              data-testid="hero-stats-row"
            >
              <div className="stat-card flex flex-col items-start" data-testid="stat-matches">
                {settings?.stat_matches_icon ? (
                  <img
                    src={buildAssetUrl(settings.stat_matches_icon)}
                    alt="matches"
                    className="w-10 h-10 object-contain mb-2"
                    data-testid="stat-matches-icon-img"
                  />
                ) : (
                  <Trophy className="text-[#FFD60A] mb-2" size={32} />
                )}
                <div className="text-3xl font-black text-white tabular-nums">{matches.length}</div>
                <div className="text-sm text-gray-400">{settings?.stat_matches_label || 'Pertandingan Terdekat'}</div>
              </div>
              <div className="stat-card flex flex-col items-start" data-testid="stat-prizes">
                {settings?.stat_prizes_icon ? (
                  <img
                    src={buildAssetUrl(settings.stat_prizes_icon)}
                    alt="prizes"
                    className="w-10 h-10 object-contain mb-2"
                    data-testid="stat-prizes-icon-img"
                  />
                ) : (
                  <Gift className="text-[#22C55E] mb-2" size={32} />
                )}
                <div className="text-3xl font-black text-white tabular-nums">{settings?.total_prize_count || ''}</div>
                <div className="text-sm text-gray-400">{settings?.stat_prizes_label || 'Hadiah'}</div>
              </div>

              {settings?.enable_countdown && settings?.countdown_date && (
                <div className="countdown-box" data-testid="countdown-timer">
                  <h3 className="text-xl font-bold mb-4">Countdown Event</h3>
                  <Countdown date={new Date(settings.countdown_date)} renderer={countdownRenderer} />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 container mx-auto px-4" data-testid="matches-rundown">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">{settings?.rundown_title || 'Rundown Pertandingan'}</h2>
          <p className="text-gray-400 text-lg">{settings?.rundown_subtitle || 'Pilih pertandingan dan tebak skornya untuk raih hadiah'}</p>
        </div>

        {loadError ? (
          <div className="max-w-xl mx-auto rounded-xl border border-red-500/40 bg-red-500/10 px-6 py-5 text-center" data-testid="home-load-error">
            <p className="text-red-300 text-base font-semibold mb-3">{loadError}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary text-sm"
              data-testid="home-load-error-retry-btn"
            >
              Muat Ulang
            </button>
          </div>
        ) : matches.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">Belum ada pertandingan hari ini</p>
          </div>
        ) : (
          <div className="flex flex-wrap justify-center gap-6 max-w-7xl mx-auto">
            {matches.map((match) => (
              <InlinePredictionCard
                key={match.id}
                match={match}
                websites={websites}
                onTermsClick={() => setShowTerms(true)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="py-16 container mx-auto px-4 border-t border-white/5" data-testid="home-check-ticket-section">
        <div className="max-w-2xl mx-auto text-center">
          <Ticket className="inline-block text-[#FFD60A] mb-3" size={48} />
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3 tracking-tight">Cek Tiket Kamu</h2>
          <p className="text-gray-400 mb-6">Masukkan kode tiket untuk melihat detail prediksi kamu.</p>
          <form onSubmit={handleCheckTicket} className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={ticketCode}
              onChange={(e) => setTicketCode(e.target.value)}
              placeholder="Contoh: yFvAVWZmSk-jOzuT4Anh-g"
              className="flex-1"
              data-testid="home-ticket-code-input"
            />
            <button
              type="submit"
              className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap"
              data-testid="home-check-ticket-btn"
            >
              <Search size={18} /> Cek Tiket
            </button>
          </form>
        </div>
      </section>

      {partners.length > 0 && (
        <section className="py-16 container mx-auto px-4 border-t border-white/5" data-testid="partner-resmi-section">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-2 tracking-tight">{settings?.partners_section_title || 'PARTNER RESMI EXTRA TIME'}</h2>
            <p className="text-gray-400">{settings?.partners_section_subtitle || 'Dipercaya oleh brand-brand terbaik'}</p>
          </div>
          <div className="partner-container">
            {partners.map((partner) => {
              const logo = buildAssetUrl(partner.logo);
              const CardTag = partner.website_url ? 'a' : 'div';
              const linkProps = partner.website_url ? { href: partner.website_url, target: '_blank', rel: 'noopener noreferrer' } : {};
              return (
                <CardTag
                  key={partner.id}
                  {...linkProps}
                  className="partner-card"
                  data-testid={`home-partner-${partner.id}`}
                  title={partner.name}
                >
                  {logo ? (
                    <img src={logo} alt={partner.name} loading="lazy" />
                  ) : (
                    <span className="text-white/70 font-bold text-sm text-center">{partner.name}</span>
                  )}
                </CardTag>
              );
            })}
          </div>
        </section>
      )}

      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />

      <Footer />
    </div>
  );
};
